import { Tooltip } from 'antd';
import { CSSProperties, FC, ReactNode } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';

import { ButtonType } from '~src/components/Button';
import { CalculatorFooter } from '~src/components/Footer/CalculatorFooter';
import { SendOfferModalProps } from '~src/components/Modal/sendOffer';
import { Root } from '~src/components/Root';
import { SavingsWidgetWithTabs as SavingsWidgetBodil } from '~src/components/SavingsWidget';
import { SavingsWidgetProps } from '~src/components/SavingsWidget/types';
import { CALCULATOR_THANK_YOU_PAGE, CREATE_OFFER_ERROR_MESSAGE } from '~src/constants';
import { useDisplayContext } from '~src/context/DisplayContext';
import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { formatSendOffersParameters, useInsertAndSendOffers } from '~src/hooks/graphql/useOffer';
import { useFinancingProvider } from '~src/hooks/useFinancingProvider';
import { captureException } from '~src/sentry';
import { useAnalytics } from '~src/tracking/ga';

import { useCustomerContext } from '~src/context/CustomerContext';
import { useToasters } from '~src/context/ToasterContext';
import { CALCULATION_DISCLAIMER } from '~src/constants';
import { ContainerLeft, ContainerRight, styledSavingsWidget } from './style';
import { useCookie } from '~src/hooks/useCookie';

type InformationPageDesktopProps = {
  children?: ReactNode;
  SavingsWidget?: FC<SavingsWidgetProps>;
  savingsWidgetStyle?: CSSProperties;
  redirectURL?: string;

  sendButton?: { disabled: boolean; type: ButtonType };
  header?: ReactNode;
  SendOfferModal: FC<SendOfferModalProps>;
  showModal: boolean;
  setShowModal: (newShowModal: boolean) => void;
};

export const InformationPageDesktopBase = ({
  children,
  SavingsWidget = SavingsWidgetBodil,
  sendButton,
  redirectURL = CALCULATOR_THANK_YOU_PAGE,
  savingsWidgetStyle,
  header,
  SendOfferModal,
  showModal,
  setShowModal,
}: InformationPageDesktopProps) => {
  const context = usePublicCalculatorContext();
  const { customer } = useCustomerContext();

  const showToaster = useToasters();

  const { isStandalone, newOfferExpenses, offerProducts, residence, takeover } = context;

  const { disabled, type = 'callToAction' } = sendButton ?? {};

  const { heatPumpOutdoorUnit, heatPumpIndoorUnit } = offerProducts ?? {};

  const offer = {
    heatPumpOutdoorUnit,
    heatPumpIndoorUnit,
  };

  const { quoteSent, quoteOpened } = useAnalytics();

  const { leadOrigin } = useDisplayContext();
  const { provider: financingProvider } = useFinancingProvider();

  const { fetching, insertOffers: sendOffers } = useInsertAndSendOffers();

  const handleError = (error: unknown) => {
    showToaster({ message: CREATE_OFFER_ERROR_MESSAGE, type: 'error' });
    captureException(error);
    setShowModal(false);
  };

  const { cookie: utmCookie } = useCookie('utmFields');
  const { cookie: gclidCookie } = useCookie('gclid');

  return (
    <Root
      header={isStandalone ? false : header}
      footer={isStandalone ? false : <CalculatorFooter />}
      pageStyle={{ paddingTop: isStandalone ? 0 : '88px', scrollMarginTop: '100px' }}
    >
      <div style={{ display: 'flex', flexFlow: 'row nowrap', width: '100%' }}>
        <ContainerLeft>{children}</ContainerLeft>

        <ContainerRight>
          <SavingsWidget
            {...{
              style: { maxHeight: '800px', ...savingsWidgetStyle },
              wrapperStyle: styledSavingsWidget,
              savingsAfterYears: 15,
              type: 'frontend',
              products: offer,
              residence,
              button: {
                onClick: () => {
                  setShowModal(true);
                  quoteOpened(customer?.email ?? undefined);
                },
                label: 'Modtag tilbud',
                type,
                disabled,
              },
              newOfferExpenses,
              takeover,
            }}
          />
          <Tooltip title={CALCULATION_DISCLAIMER}>
            <InfoCircleOutlined />
          </Tooltip>
        </ContainerRight>
      </div>

      <SendOfferModal
        onConfirm={() =>
          sendOffers(
            formatSendOffersParameters({
              ...context,
              customer: {
                ...customer,
                utmFields: utmCookie ? JSON.parse(utmCookie) : undefined,
                googleClickId: gclidCookie,
              },
              leadOrigin,
              financingProvider,
              selectedProductTypes: ['heatPump'],
            })
          )
            .catch(handleError)
            .then(result => {
              if (result?.error) {
                handleError(result.error);
                return;
              }

              quoteSent(customer?.email ?? undefined);

              setShowModal(false);

              window.location.href = redirectURL;
            })
        }
        isLoading={fetching}
        visible={showModal}
        setVisible={setShowModal}
      />
    </Root>
  );
};
